"use client";

import * as React from "react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "../../lib/utils";

export interface CursorImageTrailProps {
  items: React.ReactNode[];
  /** Size of each trail item in px. @default 120 */
  itemSize?: number;
  /** Max simultaneous items in the trail. @default 8 */
  trailLength?: number;
  /** Minimum cursor travel (px) before spawning a new item. @default 80 */
  spawnDistance?: number;
  /** Max random rotation applied to each item in degrees. @default 20 */
  rotationRange?: number;
  /** How long (ms) each item lives before fading out on its own. @default 900 */
  lifetime?: number;
  /** Optional filter: return false to skip spawning at this cursor position. */
  canSpawn?: (clientX: number, clientY: number) => boolean;
  /** Render target — defaults to the whole window. */
  containerRef?: React.RefObject<HTMLElement>;
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

interface TrailItem {
  id: number;
  x: number;
  y: number;
  rotation: number;
  itemIndex: number;
}

let _id = 0;
const nextId = () => ++_id;

export function CursorImageTrail({
  items,
  itemSize = 120,
  trailLength = 8,
  spawnDistance = 80,
  rotationRange = 20,
  lifetime = 500,
  canSpawn,
  containerRef,
  className,
  children,
  style,
}: CursorImageTrailProps) {
  const [trail, setTrail] = React.useState<TrailItem[]>([]);
  const lastPos = React.useRef<{ x: number; y: number } | null>(null);
  const itemCounter = React.useRef(0);
  const containerElRef = React.useRef<HTMLDivElement>(null);
  const rafId = React.useRef<number | null>(null);
  const pendingPos = React.useRef<{ x: number; y: number } | null>(null);
  const timers = React.useRef<number[]>([]);

  React.useEffect(() => () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  }, []);

  React.useEffect(() => {
    const el: HTMLElement | Window =
      containerRef?.current ?? containerElRef.current ?? window;

    const onLeave = () => setTrail([]);

    const processMove = () => {
      rafId.current = null;
      const pos = pendingPos.current;
      if (!pos) return;

      if (lastPos.current) {
        const dx = pos.x - lastPos.current.x;
        const dy = pos.y - lastPos.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < spawnDistance) return;
      }

      lastPos.current = { x: pos.x, y: pos.y };

      const rotation = (Math.random() * 2 - 1) * rotationRange;
      const itemIndex = itemCounter.current % items.length;
      itemCounter.current += 1;

      const id = nextId();
      setTrail((prev) => {
        const next = [
          ...prev,
          { id, x: pos.x, y: pos.y, rotation, itemIndex },
        ];
        return next.slice(-trailLength);
      });
      const t = window.setTimeout(() => {
        setTrail((prev) => prev.filter((it) => it.id !== id));
        timers.current = timers.current.filter((x) => x !== t);
      }, lifetime);
      timers.current.push(t);
    };

    const onMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      if (canSpawn && !canSpawn(mouseEvent.clientX, mouseEvent.clientY)) return;
      const rect =
        containerRef?.current?.getBoundingClientRect() ??
        containerElRef.current?.getBoundingClientRect();

      const x = rect ? mouseEvent.clientX - rect.left : mouseEvent.clientX;
      const y = rect ? mouseEvent.clientY - rect.top : mouseEvent.clientY;

      pendingPos.current = { x, y };

      if (rafId.current == null) {
        rafId.current = requestAnimationFrame(processMove);
      }
    };

    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [items, spawnDistance, rotationRange, trailLength, lifetime, canSpawn, containerRef]);

  const total = trail.length;

  return (
    <div
      ref={containerElRef}
      className={cn("relative overflow-hidden", className)}
      style={style}
    >
      {children}

      <AnimatePresence>
        {trail.map((item, i) => {
          const age = total - 1 - i;
          const scale = 0.7 + 0.3 * (1 - age / trailLength);

          return (
            <motion.div
              key={item.id}
              className="pointer-events-none absolute select-none will-change-transform"
              style={{
                left: item.x,
                top: item.y,
                width: itemSize,
                x: "-50%",
                y: "-50%",
                zIndex: i,
              }}
              initial={{
                opacity: 0,
                scale: 0.6,
                rotate: item.rotation,
              }}
              animate={{
                opacity: 1,
                scale,
                rotate: item.rotation,
              }}
              exit={{
                opacity: [1, 0.9, 0],
                scale: scale * 0.85,
                x: ["-50%", "-42%", "-56%", "-20%"],
                y: ["-50%", "-52%", "-56%", "-75%"],
                rotate: [item.rotation, item.rotation + 9, item.rotation - 7, item.rotation + 22],
                transition: {
                  duration: 0.6,
                  ease: "easeInOut",
                  opacity: { duration: 0.6, times: [0, 0.4, 1], ease: "easeIn" },
                },
              }}
              transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1],
              }}
            >
              <div className="w-full [&>svg]:h-auto [&>svg]:w-full [&>img]:h-auto [&>img]:w-full">
                {items[item.itemIndex]}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default CursorImageTrail;